import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Text, Button, Table, Badge, Group, Modal, Stack,
  TextInput, MultiSelect, Switch, ActionIcon, Tooltip, Select,
  Center, Loader, PasswordInput, Alert, SimpleGrid, Divider, Paper,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import {
  IconPlus, IconEdit, IconTrash, IconInfoCircle, IconBrandLine, IconCircleCheck, IconUnlink, IconRotateClockwise,
  IconBuilding, IconShield, IconWorld, IconSearch, IconSignature,
} from '@tabler/icons-react'
import { getUsers, createUser, updateUser, deleteUser, reactivateUser, getDepartments, getAccessLevels, adminUnlinkUserLine, getUserSignatureHistory } from '../api/client'

// ─── Role definitions ─────────────────────────────────────────────────────────

const ROLES = [
  { value: 'ADMIN',         label: 'Admin' },
  { value: 'ASSET_MANAGER', label: 'Asset Manager' },
  { value: 'AUDITOR',       label: 'Auditor' },
  { value: 'USER',          label: 'User' },
]

const ROLE_COLOR: Record<string, string> = {
  ADMIN: 'red', ASSET_MANAGER: 'violet', AUDITOR: 'blue', USER: 'gray',
}

const getRoleDesc = (t: any): Record<string, string> => ({
  ADMIN:         t('users.roleDescAdmin','สิทธิ์เต็ม — ทุกแผนก'),
  ASSET_MANAGER: t('users.roleDescManager','จัดการทรัพย์สิน — กำหนดขอบเขตตามแผนก'),
  AUDITOR:       t('users.roleDescAuditor','อ่านอย่างเดียว — จำกัดตามแผนก'),
  USER:          t('users.roleDescUser','ดูและยืมทรัพย์สิน — จำกัดตามแผนก'),
})

const EMPTY = {
  username: '', password: '', fullName: '', email: '',
  position: '', phone: '',
  roles: [] as string[],
  departments: [] as string[],
  isActive: true,
  accessLevelId: null as number | null,
  preferredLanguage: 'th' as string,
}

// ─── Scope badge helper ────────────────────────────────────────────────────────

function ScopeBadge({ roles, departments, depts }: {
  roles: any[]; departments: any[]; depts: any[]
}) {
  const { t } = useTranslation()
  const roleCodes = roles.map((r: any) => r.code ?? r)
  const isAdmin = roleCodes.includes('ADMIN')
  const isMgr = roleCodes.includes('ASSET_MANAGER')
  const deptCodes = departments.map((d: any) => d.code ?? d)

  if (isAdmin) {
    return (
      <Badge size="xs" color="violet" variant="light" leftSection={<IconWorld size={10} />}>
        {t('users.allDepartments', 'All Departments')}
      </Badge>
    )
  }
  if (isMgr && deptCodes.length === 0) {
    return (
      <Badge size="xs" color="violet" variant="light" leftSection={<IconWorld size={10} />}>
        {t('users.globalManager', 'Global Manager')}
      </Badge>
    )
  }
  if (deptCodes.length === 0) {
    return <Badge size="xs" color="red" variant="light">{t('users.noAccess', 'No Access')}</Badge>
  }
  return (
    <Group gap={4} wrap="wrap">
      {deptCodes.map((code) => {
        const d = depts.find((x) => x.code === code)
        return (
          <Badge key={code} size="xs" color="teal" variant="light" leftSection={<IconBuilding size={10} />}>
            {d?.name ?? code}
          </Badge>
        )
      })}
    </Group>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const { t } = useTranslation()
  const ROLE_DESC = getRoleDesc(t)
  const { isMobile, isTablet, isSmall } = useBreakpoint()
  const [users, setUsers]     = useState<any[]>([])
  const [depts, setDepts]     = useState<any[]>([])
  const [accessLevels, setAccessLevels] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving]   = useState(false)
  const [opened, { open, close }] = useDisclosure(false)
  const [sigHistoryOpened, { open: openSigHistory, close: closeSigHistory }] = useDisclosure(false)
  const [sigHistoryTarget, setSigHistoryTarget] = useState<any>(null)
  const [sigHistory, setSigHistory] = useState<any[]>([])
  const [sigHistoryLoading, setSigHistoryLoading] = useState(false)
  const [editing, setEditing] = useState<any>(null)
  const [form, setForm]       = useState({ ...EMPTY })
  const [search, setSearch]         = useState('')
  const [roleFilter, setRoleFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    const [u, d, al] = await Promise.all([getUsers(), getDepartments(), getAccessLevels()])
    setUsers(u.data)
    setDepts(d.data.filter((x: any) => x.isActive !== false))
    setAccessLevels(al.data ?? [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const openAdd = () => { setEditing(null); setForm({ ...EMPTY }); open() }
  const openEdit = (u: any) => {
    setEditing(u)
    setForm({
      username:    u.username,
      password:    '',
      fullName:    u.fullName  ?? '',
      email:       u.email     ?? '',
      position:    u.position  ?? '',
      phone:       u.phone     ?? '',
      roles:       u.roles?.map((r: any) => r.code ?? r) ?? [],
      departments: u.departments?.map((d: any) => d.code ?? d) ?? [],
      isActive:    u.isActive  ?? true,
      accessLevelId: u.accessLevelId ?? null,
      preferredLanguage: u.preferredLanguage ?? 'th',
    })
    open()
  }

  const handleSave = async () => {
    if (!form.username) {
      notifications.show({ color: 'red', message: 'Username is required' }); return
    }
    if (!editing && !form.password) {
      notifications.show({ color: 'red', message: 'Password is required for new users' }); return
    }
    if (form.roles.length === 0) {
      notifications.show({ color: 'red', message: 'At least one role is required' }); return
    }
    const isAdminRole = form.roles.includes('ADMIN')
    const isManagerRole = form.roles.includes('ASSET_MANAGER')
    const needsDept = !isAdminRole && !isManagerRole
    if (needsDept && form.departments.length === 0) {
      notifications.show({ color: 'orange', message: t('users.deptRequired','USER / AUDITOR ต้องมีอย่างน้อย 1 แผนก') }); return
    }
    setSaving(true)
    try {
      const payload: any = { ...form }
      if (!payload.password) delete payload.password
      if (editing) await updateUser(editing.id, payload)
      else await createUser(payload)
      notifications.show({ color: 'lime', message: editing ? 'User updated' : 'User created' })
      close(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setSaving(false) }
  }

  const handleDeactivate = (u: any) => {
    modals.openConfirmModal({
      title: t('users.deactivateUserTitle', 'Deactivate User'),
      children: <Text size="sm">{t('users.deactivateUserBody', 'Deactivate {{username}}? They will not be able to sign in, and their LINE connection will be disconnected. Can be reactivated later.', { username: u.username })}</Text>,
      labels: { confirm: t('common.deactivate','ปิดใช้งาน'), cancel: t('common.cancel','ยกเลิก') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await deleteUser(u.id)
        notifications.show({ color: 'lime', message: 'User deactivated' })
        load()
      },
    })
  }

  const handleReactivate = (u: any) => {
    modals.openConfirmModal({
      title: t('users.reactivateUserTitle', 'Reactivate User'),
      children: <Text size="sm">{t('users.reactivateUserBody', 'Reactivate {{username}}? They will be able to sign in again. They will need to link LINE again themselves if they want notifications.', { username: u.username })}</Text>,
      labels: { confirm: t('users.reactivate', 'Reactivate'), cancel: t('common.cancel','ยกเลิก') },
      confirmProps: { color: 'teal' },
      onConfirm: async () => {
        await reactivateUser(u.id)
        notifications.show({ color: 'lime', message: t('users.reactivatedSuccess', 'User reactivated') })
        load()
      },
    })
  }

  const handleViewSignatureHistory = async (u: any) => {
    setSigHistoryTarget(u)
    openSigHistory()
    setSigHistoryLoading(true)
    try {
      const res = await getUserSignatureHistory(u.id)
      setSigHistory(res.data)
    } catch {
      notifications.show({ color: 'red', message: t('common.error', 'Something went wrong') })
    } finally { setSigHistoryLoading(false) }
  }

  const handleAdminUnlinkLine = (u: any) => {
    modals.openConfirmModal({
      title: t('users.lineUnlinkTitle', 'Unlink LINE Account'),
      children: <Text size="sm">{t('users.lineUnlinkBody', "Unlink {{username}}'s LINE account? They will need to link again themselves from their own Profile page.", { username: u.username })}</Text>,
      labels: { confirm: t('users.lineForceUnlink', 'Unlink'), cancel: t('common.cancel','ยกเลิก') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await adminUnlinkUserLine(u.id)
        notifications.show({ color: 'lime', message: t('users.lineUnlinkedSuccess', 'LINE account unlinked') })
        load()
      },
    })
  }

  const f = (k: string) => (v: any) => setForm((p) => ({ ...p, [k]: v }))

  const deptOptions = depts.map((d) => ({ value: d.code, label: `${d.name}  (${d.code})` }))

  // ── Computed form state ────────────────────────────────────────────────────
  const isAdmin   = form.roles.includes('ADMIN')
  const isMgr     = form.roles.includes('ASSET_MANAGER')
  const isGlobalMgr = isMgr && form.departments.length === 0
  const isDeptMgr   = isMgr && form.departments.length > 0

  return (
    <Box p={isSmall ? 16 : 32}>
      <Group justify="space-between" mb={isMobile ? 'md' : 'xl'} wrap="nowrap">
        <Box style={{ minWidth: 0 }}>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px' }}>
            {t('users.title','จัดการผู้ใช้งาน')}
          </Text>
          {!isMobile && <Text size="sm" c="dimmed">{t('users.subtitle','จัดการผู้ใช้และสิทธิ์การเข้าถึงตามแผนก')}</Text>}
        </Box>
        <Button leftSection={<IconPlus size={16} />} onClick={openAdd}
          style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }} size={isMobile ? 'sm' : 'md'}>
          {isMobile ? t('users.addUser','เพิ่มผู้ใช้') : t('users.addUser','เพิ่มผู้ใช้')}
        </Button>
      </Group>

      {/* Role guide — desktop only */}
      {!isMobile && (
        <Paper withBorder p="md" radius="lg" mb="lg" style={{ background: '#f8f7f4' }}>
          <Text size="sm" fw={700} mb={10} style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('users.roleGuideTitle','คู่มือบทบาทและแผนก')}
          </Text>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
            {[
              { role: 'ADMIN',         color: 'red',    icon: IconShield, scope: t('users.roleDescAdmin','สิทธิ์เต็ม — ทุกแผนก') },
              { role: 'ASSET_MANAGER', color: 'violet', icon: IconBuilding, scope: t('users.scopeManager','ไม่มีแผนก = global / มีแผนก = scoped') },
              { role: 'AUDITOR',       color: 'blue',   icon: IconShield, scope: t('users.scopeAuditor','อ่านอย่างเดียว จำกัดตามแผนก') },
              { role: 'USER',          color: 'gray',   icon: IconBuilding, scope: t('users.scopeUser','ดูและยืม จำกัดตามแผนก') },
            ].map((r) => (
              <Box key={r.role} p={10} style={{ background: '#fff', borderRadius: 10, border: '1px solid #ebebeb' }}>
                <Badge color={r.color} variant="light" size="sm" mb={6}>{r.role}</Badge>
                <Text size="xs" c="dimmed">{r.scope}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Paper>
      )}

      {/* Search + Filter bar */}
      <Group mb="md" gap="sm" wrap={isMobile ? 'wrap' : 'nowrap'}>
        <TextInput
          placeholder={t('users.searchPlaceholder','ค้นหาชื่อผู้ใช้, ชื่อ, อีเมล...')}
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, width: isMobile ? '100%' : undefined }}
          styles={{ input: { borderColor: '#e2e8f0' } }}
        />
        {!isMobile && (
          <>
            <Select
              placeholder={t('users.allRoles','ทุกบทบาท')}
              clearable
              value={roleFilter}
              onChange={setRoleFilter}
              data={ROLES}
              style={{ width: 160 }}
              styles={{ input: { borderColor: '#e2e8f0' } }}
            />
            <Select
              placeholder={t('users.allStatus','ทุกสถานะ')}
              clearable
              value={statusFilter}
              onChange={setStatusFilter}
              data={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
              style={{ width: 140 }}
              styles={{ input: { borderColor: '#e2e8f0' } }}
            />
          </>
        )}
      </Group>

      {/* Table / Card list */}
      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', overflow: 'auto' }}>
        {loading ? <Center p="xl"><Loader color="teal" /></Center> : (() => {
          const q = search.toLowerCase()
          const filtered = users.filter(u => {
            const matchSearch = !q || u.username?.toLowerCase().includes(q) || u.fullName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
            const matchRole = !roleFilter || u.roles?.some((r: any) => (r.code ?? r) === roleFilter)
            const matchStatus = !statusFilter || (statusFilter === 'active' ? u.isActive : !u.isActive)
            return matchSearch && matchRole && matchStatus
          })

          if (filtered.length === 0) return (
            <Center p="xl"><Text c="dimmed">{t("users.noUsersFound","No users found")}</Text></Center>
          )

          /* ── Mobile: card list ── */
          if (isMobile) return (
            <Stack gap={0}>
              {filtered.map(u => (
                <Box key={u.id} p="md" style={{ borderBottom: '1px solid #f1f5f9', opacity: u.isActive ? 1 : 0.55 }}>
                  <Group justify="space-between" wrap="nowrap" mb={6}>
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" fw={700} style={{ fontFamily: 'Inter, sans-serif' }} truncate>{u.username}</Text>
                        <Badge color={u.isActive ? 'lime' : 'red'} variant="light" size="xs">
                          {u.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Group>
                      {u.fullName && <Text size="xs" c="dimmed" truncate>{u.fullName}</Text>}
                    </Box>
                    <Group gap={4} wrap="nowrap">
                      <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(u)} size="sm">
                        <IconEdit size={15} />
                      </ActionIcon>
                      {u.isActive && (
                        <ActionIcon variant="subtle" color="red" onClick={() => handleDeactivate(u)} size="sm">
                          <IconTrash size={15} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Group>
                  <Group gap={4} wrap="wrap" mb={4}>
                    {u.roles?.map((r: any) => (
                      <Badge key={r.code ?? r} size="xs" color={ROLE_COLOR[r.code ?? r] ?? 'gray'} variant="light">
                        {r.code ?? r}
                      </Badge>
                    ))}
                  </Group>
                  <ScopeBadge roles={u.roles ?? []} departments={u.departments ?? []} depts={depts} />
                  {u.email && <Text size="xs" c="dimmed" mt={4}>{u.email}</Text>}
                </Box>
              ))}
            </Stack>
          )

          /* ── Desktop: full table ── */
          return (
            <Table highlightOnHover>
              <Table.Thead style={{ background: '#f8f7f4' }}>
                <Table.Tr>
                  <Table.Th>{t("common.username","Username")}</Table.Th>
                  <Table.Th>{t("common.fullName","Full Name")}</Table.Th>
                  <Table.Th>{t("users.emailPosition","Email / Position")}</Table.Th>
                  <Table.Th>{t("common.roles","Roles")}</Table.Th>
                  <Table.Th>
                    <Group gap={4}>
                      <IconBuilding size={14} />
                      <Text size="sm" fw={600}>{t("users.departmentScope","Department Scope")}</Text>
                    </Group>
                  </Table.Th>
                  <Table.Th>{t("common.status","Status")}</Table.Th>
                  <Table.Th>
                    <Group gap={4}>
                      <IconBrandLine size={14} color="#06C755" />
                      <Text size="sm" fw={600}>LINE</Text>
                    </Group>
                  </Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((u) => (
                  <Table.Tr key={u.id} style={{ opacity: u.isActive ? 1 : 0.5 }}>
                    <Table.Td>
                      <Text size="sm" fw={700} style={{ fontFamily: 'Inter, sans-serif' }}>{u.username}</Text>
                    </Table.Td>
                    <Table.Td><Text size="sm">{u.fullName ?? '-'}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{u.email ?? '-'}</Text>
                      {u.position && <Text size="xs" c="dimmed">{u.position}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        {u.roles?.map((r: any) => (
                          <Badge key={r.code ?? r} size="xs"
                            color={ROLE_COLOR[r.code ?? r] ?? 'gray'} variant="light">
                            {r.code ?? r}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <ScopeBadge roles={u.roles ?? []} departments={u.departments ?? []} depts={depts} />
                    </Table.Td>
                    <Table.Td>
                      <Badge color={u.isActive ? 'lime' : 'red'} variant="light" size="sm">
                        {u.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {u.lineUserId ? (
                        <Group gap={4} wrap="nowrap">
                          <Badge color="teal" variant="light" size="xs" leftSection={<IconCircleCheck size={11} />}>
                            {t('users.lineLinked', 'Linked')}
                          </Badge>
                          <Tooltip label={t('users.lineForceUnlink', 'Unlink (e.g. employee left / lost phone)')}>
                            <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleAdminUnlinkLine(u)}>
                              <IconUnlink size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      ) : (
                        <Text size="xs" c="dimmed">{t('users.lineNotLinkedShort', 'Not linked')}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label={t("users.viewSignatureHistory","View Signature History")}>
                          <ActionIcon variant="subtle" color="grape" onClick={() => handleViewSignatureHistory(u)}>
                            <IconSignature size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t("common.edit","Edit")}>
                          <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(u)}>
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {u.isActive ? (
                          <Tooltip label={t("common.deactivate","Deactivate")}>
                            <ActionIcon variant="subtle" color="red" onClick={() => handleDeactivate(u)}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        ) : (
                          <Tooltip label={t("users.reactivate","Reactivate")}>
                            <ActionIcon variant="subtle" color="teal" onClick={() => handleReactivate(u)}>
                              <IconRotateClockwise size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )
        })()}
      </Box>

      {/* ── Modal ── */}
      <Modal opened={opened} onClose={close} size={isSmall ? '100%' : 'lg'} radius={isMobile ? 0 : 'lg'} fullScreen={isSmall}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{editing ? t('users.editUser','แก้ไขผู้ใช้') : t('users.addUser','เพิ่มผู้ใช้')}</Text>}>
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label={t('users.username','ชื่อผู้ใช้ *')} value={form.username} onChange={(e) => f('username')(e.target.value)} disabled={!!editing} />
            <PasswordInput label={editing ? t('users.newPassword','รหัสผ่านใหม่ (เว้นว่างถ้าไม่เปลี่ยน)') : t('users.password','รหัสผ่าน *')}
              value={form.password} onChange={(e) => f('password')(e.target.value)} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label={t('users.fullName','ชื่อ-นามสกุล')} value={form.fullName} onChange={(e) => f('fullName')(e.target.value)} />
            <TextInput label={t('users.email','อีเมล')} value={form.email} onChange={(e) => f('email')(e.target.value)} />
            <TextInput label={t('users.position','ตำแหน่ง')} value={form.position} onChange={(e) => f('position')(e.target.value)} />
            <TextInput label={t('users.phone','โทรศัพท์')} value={form.phone} onChange={(e) => f('phone')(e.target.value)} />
          </SimpleGrid>

          <Divider label={t('users.accessControl','การควบคุมสิทธิ์')} labelPosition="left" />

          {/* Roles */}
          <MultiSelect
            label={t('users.roles','บทบาท *')}
            data={ROLES}
            value={form.roles}
            onChange={f('roles')}
            searchable
          />
          {form.roles.length > 0 && (
            <Box pl={4}>
              {form.roles.map((r) => (
                <Text key={r} size="xs" c="dimmed">• <b>{r}:</b> {ROLE_DESC[r] ?? ''}</Text>
              ))}
            </Box>
          )}

          {/* Departments */}
          <Box>
            <MultiSelect
              label={
                <Group gap={6}>
                  <IconBuilding size={14} />
                  <Text size="sm" fw={500}>{t("users.departments","Departments")}</Text>
                  {!isAdmin && <Text size="xs" c="dimmed">{t('users.deptScope','กำหนดขอบเขตการมองเห็นทรัพย์สิน')}</Text>}
                </Group>
              }
              placeholder={isAdmin ? t('users.notRequiredForAdmin','Admin เข้าถึงได้ทุกแผนก') : t('users.selectDepts','เลือกแผนก...')}
              data={deptOptions}
              value={form.departments}
              onChange={f('departments')}
              searchable
              disabled={isAdmin}
            />

            {/* Scope preview */}
            {!isAdmin && (
              <Box mt={8} p={12} style={{
                borderRadius: 10,
                border: '1px solid',
                borderColor: isDeptMgr ? '#a78bfa' : isGlobalMgr ? '#a78bfa' : form.departments.length > 0 ? '#99f6e4' : '#fca5a5',
                background: isDeptMgr ? '#faf5ff' : isGlobalMgr ? '#faf5ff' : form.departments.length > 0 ? '#f0fdf4' : '#fff1f2',
              }}>
                {isGlobalMgr && (
                  <>
                    <Group gap={6} mb={4}>
                      <IconWorld size={14} color="#7c3aed" />
                      <Text size="xs" fw={700} c="violet.7">{t("users.globalAssetManager","Global Asset Manager")}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">No department assigned → {t('users.globalManagerDesc','User นี้จัดการทรัพย์สินทุกแผนกได้')}</Text>
                  </>
                )}
                {isDeptMgr && (
                  <>
                    <Group gap={6} mb={4}>
                      <IconBuilding size={14} color="#7c3aed" />
                      <Text size="xs" fw={700} c="violet.7">{t("users.departmentAssetManager","Department Asset Manager")}</Text>
                    </Group>
                    <Text size="xs" c="dimmed" mb={6}>This user manages assets and configuration <b>only</b> for:</Text>
                    <Group gap={6}>
                      {form.departments.map((code) => {
                        const d = depts.find((x) => x.code === code)
                        return <Badge key={code} size="sm" color="violet" variant="light" leftSection={<IconBuilding size={10} />}>{d?.name ?? code}</Badge>
                      })}
                    </Group>
                    <Text size="xs" c="dimmed" mt={6}>✓ Can add/edit/delete assets, categories, and sub-categories for their department(s)</Text>
                    <Text size="xs" c="dimmed">✗ Cannot see or modify assets/config from other departments</Text>
                  </>
                )}
                {!isMgr && form.departments.length > 0 && (
                  <>
                    <Text size="xs" fw={600} c="teal.7" mb={4}>{t('users.assetVisibility','ขอบเขตทรัพย์สินที่มองเห็น')}:</Text>
                    <Group gap={6}>
                      {form.departments.map((code) => {
                        const d = depts.find((x) => x.code === code)
                        return <Badge key={code} size="sm" color="teal" variant="light" leftSection={<IconBuilding size={10} />}>{d?.name ?? code}</Badge>
                      })}
                    </Group>
                  </>
                )}
                {!isMgr && form.departments.length === 0 && (
                  <Text size="xs" c="red.6">⚠ {t('users.noDeptWarning','ไม่มีแผนก — User นี้จะไม่เห็นทรัพย์สินใดๆ')}</Text>
                )}
              </Box>
            )}
          </Box>

          {/* Default Language */}
          <Select
            label="🌐 ภาษาเริ่มต้น"
            description="ระบบจะใช้ภาษานี้เมื่อ User นี้ Login"
            data={[
              { value: 'th', label: '🇹🇭 ภาษาไทย' },
              { value: 'en', label: '🇬🇧 English' },
            ]}
            value={form.preferredLanguage}
            onChange={(v) => f('preferredLanguage')(v ?? 'th')}
            style={{ maxWidth: 220 }}
          />

          <Divider />

          {/* Access Level */}
          {!isAdmin && (
            <Box>
              <Text size="sm" fw={600} mb={4}>🔐 Access Level <Text component="span" size="xs" c="dimmed">(ไม่บังคับ)</Text></Text>
              <Text size="xs" c="dimmed" mb={8}>กำหนดว่า User นี้เข้าหน้าไหนได้บ้าง — ถ้าไม่เลือกจะเห็นทุกหน้าที่ Tenant เปิดไว้</Text>
              <Select
                placeholder="ไม่จำกัด (เห็นทุกหน้าที่เปิด)"
                clearable
                data={[
                  ...accessLevels.map((al: any) => ({
                    value: String(al.id),
                    label: al.name + (al.description ? ` — ${al.description}` : ''),
                  }))
                ]}
                value={form.accessLevelId ? String(form.accessLevelId) : null}
                onChange={(v) => f('accessLevelId')(v ? Number(v) : null)}
              />
              {accessLevels.length === 0 && (
                <Text size="xs" c="dimmed" mt={4}>ยังไม่มี Access Level — ไปสร้างที่ ตั้งค่าระบบ → Access Level ก่อน</Text>
              )}
            </Box>
          )}

          <Divider />
          <Switch label={t('users.activeAccount','บัญชีใช้งานได้')} description={t('users.inactiveHint','บัญชีที่ปิดใช้งานจะไม่สามารถ Login ได้')}
            checked={form.isActive} onChange={(e) => f('isActive')(e.currentTarget.checked)} />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={close}>{t('common.cancel','ยกเลิก')}</Button>
            <Button loading={saving} onClick={handleSave}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {editing ? t('users.saveChanges','บันทึก') : t('users.createUser','สร้างผู้ใช้')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Signature History Modal ── */}
      <Modal opened={sigHistoryOpened} onClose={closeSigHistory} size="md" radius="lg"
        title={<Text fw={600}>{t('users.signatureHistoryTitle', 'Signature History')} — {sigHistoryTarget?.username}</Text>}>
        {sigHistoryLoading ? (
          <Center p="xl"><Loader color="teal" size="sm" /></Center>
        ) : sigHistory.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">{t('users.noSignatureHistory', 'No signature changes recorded yet.')}</Text>
        ) : (
          <Stack gap="sm">
            {sigHistory.map((h: any) => (
              <Paper key={h.id} withBorder radius="md" p="sm">
                <Group justify="space-between" mb={6}>
                  <Badge size="sm" color={h.action === 'CREATED' ? 'teal' : h.action === 'DELETED' ? 'red' : 'orange'}>
                    {h.action === 'CREATED' ? t('users.sigCreated','Added') : h.action === 'DELETED' ? t('users.sigDeleted','Removed') : t('users.sigUpdated','Changed')}
                  </Badge>
                  <Text size="xs" c="dimmed">{new Date(h.changedAt).toLocaleString('th-TH')}</Text>
                </Group>
                <SimpleGrid cols={2} spacing="xs">
                  <Box>
                    <Text size="xs" c="dimmed" mb={2}>{t('users.sigBefore','Before')}</Text>
                    {h.previousImage
                      ? <img src={h.previousImage} alt="previous signature" style={{ maxWidth: '100%', maxHeight: 60, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }} />
                      : <Text size="xs" c="dimmed" fs="italic">{t('users.sigNone','(none)')}</Text>}
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed" mb={2}>{t('users.sigAfter','After')}</Text>
                    {h.newImage
                      ? <img src={h.newImage} alt="new signature" style={{ maxWidth: '100%', maxHeight: 60, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }} />
                      : <Text size="xs" c="dimmed" fs="italic">{t('users.sigNone','(none)')}</Text>}
                  </Box>
                </SimpleGrid>
                {h.changedBy && <Text size="xs" c="dimmed" mt={6}>{t('users.sigChangedBy','Changed by')}: {h.changedBy}</Text>}
              </Paper>
            ))}
          </Stack>
        )}
      </Modal>
    </Box>
  )
}
